import React, { useState, useEffect } from 'react';
import { Row } from 'antd';
import { PixType } from '@portal/enum/pix';
import { validateEmail } from '@portal/utils/validate';
import { translate } from '@portal/services/i18n';
import ThemedText from '@portal/components/ThemedText/ThemedText';
import OtpInput from '@portal/components/OtpInput/OtpInput';

interface IProps {
  newPixKey?: string;
  setVerificationCode?: (value: string) => void;
  handleResendCode?: () => void;
  pixType?: number;
}

const ConfirmationPix = (props: IProps) => {
  const [count, setCount] = useState(60);
  const [resendAvailable, setResendAvailable] = useState(false);

  useEffect(() => {
    const timer = () => {
      if (count > 0 && !resendAvailable) {
        setTimeout(() => setCount(count - 1), 1000);
      } else {
        setCount(60);
        setResendAvailable(true);
      }
    };

    timer();
  }, [count, resendAvailable]);

  const handleResend = () => {
    if (resendAvailable) {
      setCount(60);
      setResendAvailable(false);

      if (props.handleResendCode) {
        props.handleResendCode();
      }
    }
  };

  return (
    <section
      className="confirmation-pix"
      aria-labelledby="confirmation-pix-instruction"
    >
      <ThemedText
        as="h2"
        id="confirmation-pix-instruction"
        className="confirmation-pix__instruction"
      >
        {`${translate('COMPONENTS.CONFIRMATION_PIX.INSTRUCTION')} ${
          props.pixType === PixType.PHONE ||
          !validateEmail(props.newPixKey ?? '')
            ? translate('COMPONENTS.CONFIRMATION_PIX.CELLPHONE')
            : translate('COMPONENTS.CONFIRMATION_PIX.EMAIL')
        }`}
      </ThemedText>
      <ThemedText as="p" className="confirmation-pix__new-pix-key">
        {`${translate('COMPONENTS.CONFIRMATION_PIX.SENT_TO')} ${
          props.pixType === PixType.PHONE ||
          !validateEmail(props.newPixKey ?? '')
            ? translate('COMPONENTS.CONFIRMATION_PIX.NUMBER')
            : translate('COMPONENTS.CONFIRMATION_PIX.EMAIL')
        } ${props.newPixKey}`}
      </ThemedText>
      <Row data-sentry-mask>
        <OtpInput
          aria-labelledby="confirmation-pix-instruction"
          autoFocus
          maxLength={6}
          placeholder="–"
          onComplete={(value) =>
            props.setVerificationCode && props.setVerificationCode(value)
          }
          onChange={(value) =>
            props.setVerificationCode && props.setVerificationCode(value)
          }
          containerClassName="confirmation-pix__code-input"
        />
      </Row>
      <div className="resend-code">
        <ThemedText>
          {translate(
            'PAGES.AUTH.CHANGE_CREDENTIALS.PASSWORD.CODE.DIDNT_RECEIVE'
          )}
        </ThemedText>
        {resendAvailable ? (
          <div className="resend-code__counter">
            <button
              type="button"
              className="resend-code__button"
              onClick={handleResend}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleResend();
                }
              }}
              aria-label={
                props.pixType === PixType.PHONE ||
                !validateEmail(props.newPixKey ?? '')
                  ? translate('COMPONENTS.CONFIRMATION_PIX.RESEND_CELLPHONE')
                  : translate('COMPONENTS.CONFIRMATION_PIX.RESEND_EMAIL')
              }
            >
              <ThemedText as="span" variant="body">
                {props.pixType === PixType.PHONE ||
                !validateEmail(props.newPixKey ?? '')
                  ? translate('COMPONENTS.CONFIRMATION_PIX.RESEND_CELLPHONE')
                  : translate('COMPONENTS.CONFIRMATION_PIX.RESEND_EMAIL')}
              </ThemedText>
            </button>
          </div>
        ) : (
          <ThemedText aria-live="polite">{`${count} ${translate(
            'PAGES.AUTH.CHANGE_CREDENTIALS.PASSWORD.CODE.SECONDS'
          )}`}</ThemedText>
        )}
      </div>
    </section>
  );
};

export default ConfirmationPix;
